import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowRight, Users, Clock, Star } from 'lucide-react';
import Slider from 'react-slick';
import { useCurrency } from '../context/CurrencyContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [packages, setPackages] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [galleryItems, setGalleryItems] = useState([]);
  const [slidesToShow, setSlidesToShow] = useState(3);
  const { formatPrice } = useCurrency();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [pkgRes, testRes, galRes] = await Promise.all([
          axios.get(`${API}/packages`),
          axios.get(`${API}/testimonials`),
          axios.get(`${API}/gallery?limit=8`),
        ]);
        setPackages(pkgRes.data);
        setTestimonials(testRes.data);
        setGalleryItems(galRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  // Responsive slidesToShow — bypass react-slick's flaky `responsive` option
  useEffect(() => {
    const updateSlides = () => {
      const w = window.innerWidth;
      if (w < 768) setSlidesToShow(1);
      else if (w < 1024) setSlidesToShow(2);
      else setSlidesToShow(3);
    };
    updateSlides();
    window.addEventListener('resize', updateSlides);
    return () => window.removeEventListener('resize', updateSlides);
  }, []);

  const resolveImg = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    if (url.startsWith('/api/')) return `${BACKEND_URL}${url}`;
    return url;
  };

  // Carousel settings — desktop: 3 cards, tablet: 2, mobile: 1
  const carouselSettings = {
    dots: true,
    infinite: packages.length > slidesToShow,
    speed: 500,
    slidesToShow: slidesToShow,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 4000,
    pauseOnHover: true,
    arrows: slidesToShow > 1,
    swipeToSlide: true,
    adaptiveHeight: false,
  };

  return (
    <div className="bg-sand" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-navy"
          style={{
            backgroundImage: `url('https://images.pexels.com/photos/1926335/pexels-photo-1926335.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/50 to-black/80"></div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.5 }}
          className="relative z-10 text-center px-4 max-w-5xl"
        >
          <h1 className="font-heading text-6xl md:text-8xl font-light tracking-tight leading-[0.9] text-sand mb-6">
            The Raw<br />& The Royal
          </h1>
          <p className="font-body text-base md:text-lg text-sand/80 leading-relaxed mb-8 max-w-2xl mx-auto">
            Experience the untamed majesty of Tadoba Tiger Reserve.
            Where wilderness meets luxury in India's premier tiger habitat.
          </p>
          <Link
            to="/packages"
            data-testid="hero-explore-btn"
            className="inline-flex items-center space-x-2 bg-emerald-600 hover:bg-emerald-600/90 text-white px-8 py-6 rounded-full font-bold tracking-wide transition-all hover:scale-105"
          >
            <span>Explore Safaris</span>
            <ArrowRight size={20} />
          </Link>
        </motion.div>
      </section>

      {/* Safari Packages Carousel */}
      <section className="py-24 px-4 bg-white overflow-hidden" data-testid="packages-carousel">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl md:text-6xl font-normal leading-tight text-navy mb-4">
              Our Safari Packages
            </h2>
            <p className="font-body text-base md:text-lg text-foreground/80 leading-relaxed max-w-2xl mx-auto">
              Explore our curated collection of wildlife experiences
            </p>
          </div>

          {packages.length > 0 && (
            <Slider key={`slider-${slidesToShow}`} {...carouselSettings} className="packages-carousel">
              {packages.map((pkg) => (
                <div key={pkg.id} className="px-2 md:px-3">
                  <Link to={`/packages/${pkg.id}`}>
                    <div className="group relative overflow-hidden rounded-xl bg-white shadow-xl transition-all hover:-translate-y-2 hover:shadow-2xl border-2 border-transparent hover:border-emerald-600 h-full">
                      <div className="aspect-[4/3] overflow-hidden relative">
                        <img
                          src={pkg.image_url}
                          alt={pkg.name}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                        <div className="absolute top-4 right-4 bg-emerald-600 text-white px-4 py-2 rounded-full price-luxe-bold text-base shadow-lg flex flex-col items-center leading-tight">
                          <span>{pkg.price === 0 ? 'FREE!' : formatPrice(pkg.price)}</span>
                          {pkg.price > 0 && pkg.pricing_type === 'per_person' && (
                            <span className="text-[10px] font-body font-medium uppercase tracking-wide opacity-90">/ person</span>
                          )}
                        </div>
                        {pkg.price === 0 && (
                          <div className="absolute top-4 left-4 bg-gold text-navy px-3 py-1 rounded-full font-body font-bold text-xs">
                            COMPLIMENTARY
                          </div>
                        )}
                      </div>
                      <div className="p-6">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-accent text-xs uppercase tracking-widest text-muted-foreground">
                            {pkg.category}
                          </span>
                        </div>
                        <h3 className="font-heading text-2xl font-normal text-navy mb-3 line-clamp-2">
                          {pkg.name}
                        </h3>
                        <p className="font-body text-sm text-foreground/70 mb-4 line-clamp-2">
                          {pkg.description}
                        </p>
                        <div className="flex items-center space-x-4 text-sm text-muted-foreground mb-4">
                          <div className="flex items-center space-x-1">
                            <Clock size={16} />
                            <span>{pkg.duration}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Users size={16} />
                            <span>Up to {pkg.max_guests}</span>
                          </div>
                        </div>
                        <button className="w-full bg-emerald-600 text-white py-3 rounded-lg font-body font-semibold hover:bg-emerald-600/90 transition-all group-hover:scale-105">
                          View Details
                        </button>
                      </div>
                    </div>
                  </Link>
                </div>
              ))}
            </Slider>
          )}

          <div className="text-center mt-12">
            <Link
              to="/packages"
              data-testid="view-all-packages-btn"
              className="inline-flex items-center space-x-2 border-2 border-navy text-navy hover:bg-navy hover:text-gold rounded-full px-8 py-4 transition-all font-body font-semibold text-lg hover:scale-105"
            >
              <span>View All Packages</span>
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Safaris - Static Grid */}
      <section className="py-24 px-4 bg-sand" data-testid="featured-packages">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl md:text-6xl font-normal leading-tight text-navy mb-4">
              Popular Safaris
            </h2>
            <p className="font-body text-base md:text-lg text-foreground/80 leading-relaxed max-w-2xl mx-auto">
              Our most loved wildlife experiences
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {packages.slice(0, 3).map((pkg, index) => (
              <motion.div
                key={pkg.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                data-testid={`package-card-${index}`}
              >
                <Link to={`/packages/${pkg.id}`}>
                  <div className="group relative overflow-hidden rounded-xl bg-white shadow-xl transition-all hover:-translate-y-1 hover:shadow-2xl border-none">
                    <div className="aspect-[4/3] overflow-hidden">
                      <img
                        src={pkg.image_url}
                        alt={pkg.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-accent text-xs uppercase tracking-widest text-muted-foreground">
                          {pkg.category}
                        </span>
                        <span className="price-luxe-bold text-emerald-600 text-xl flex items-baseline gap-1">
                          {formatPrice(pkg.price)}
                          {pkg.pricing_type === 'per_person' && (
                            <span className="text-[10px] font-body font-medium uppercase tracking-wide text-muted-foreground">/person</span>
                          )}
                        </span>
                      </div>
                      <h3 className="font-heading text-2xl font-normal text-navy mb-3">
                        {pkg.name}
                      </h3>
                      <p className="font-body text-sm text-foreground/70 mb-4 line-clamp-2">
                        {pkg.description}
                      </p>
                      <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                        <div className="flex items-center space-x-1">
                          <Clock size={16} />
                          <span>{pkg.duration}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Users size={16} />
                          <span>Up to {pkg.max_guests}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/packages"
              data-testid="view-all-packages-btn"
              className="inline-flex items-center space-x-2 border border-navy text-navy hover:bg-navy hover:text-gold rounded-full px-8 py-6 transition-all"
            >
              <span className="font-body font-semibold">View All Packages</span>
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 px-4 bg-navy" data-testid="why-choose-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl md:text-6xl font-normal leading-tight text-gold mb-4">
              Why JMD WILDLIFE?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="bg-gold/10 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                <Star size={32} className="text-gold" />
              </div>
              <h3 className="font-heading text-2xl text-sand mb-3">Expert Guides</h3>
              <p className="font-body text-sand/70">
                Experienced naturalists with deep knowledge of Tadoba's wildlife
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-center"
            >
              <div className="bg-gold/10 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                <Users size={32} className="text-gold" />
              </div>
              <h3 className="font-heading text-2xl text-sand mb-3">Small Groups</h3>
              <p className="font-body text-sand/70">
                Intimate safari experiences with limited group sizes
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-center"
            >
              <div className="bg-gold/10 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                <Clock size={32} className="text-gold" />
              </div>
              <h3 className="font-heading text-2xl text-sand mb-3">Prime Timing</h3>
              <p className="font-body text-sand/70">
                Morning and evening safaris during peak wildlife activity
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      {testimonials.length > 0 && (
        <section className="py-24 px-4" data-testid="testimonials-section">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="font-heading text-4xl md:text-6xl font-normal leading-tight text-navy mb-4">
                Guest Experiences
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {testimonials.slice(0, 4).map((testimonial, index) => (
                <motion.div
                  key={testimonial.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white p-8 rounded-xl shadow-lg"
                  data-testid={`testimonial-${index}`}
                >
                  <div className="flex items-center space-x-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} size={18} className="fill-gold text-gold" />
                    ))}
                  </div>
                  <p className="font-body text-foreground/80 mb-6 italic">
                    "{testimonial.comment}"
                  </p>
                  <div>
                    <p className="font-heading text-lg text-navy font-semibold">
                      {testimonial.name}
                    </p>
                    <p className="font-body text-sm text-muted-foreground">
                      {testimonial.location}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Safari Library Preview — placed AFTER testimonials, BEFORE final CTA */}
      <section className="py-24 px-4 bg-white" data-testid="home-safari-library">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="font-accent text-xs uppercase tracking-[0.3em] text-emerald-700 mb-3 block">
              Captured by Our Guests
            </span>
            <h2 className="font-heading text-4xl md:text-6xl font-normal leading-tight text-navy mb-4">
              Safari Library
            </h2>
            <p className="font-body text-base md:text-lg text-foreground/80 leading-relaxed max-w-2xl mx-auto">
              Real moments. Real wildlife. Real guests — see what awaits you in Tadoba.
            </p>
          </div>

          {galleryItems.length === 0 ? (
            <div className="text-center py-12 bg-sand/40 rounded-xl max-w-xl mx-auto">
              <p className="font-body text-navy">
                Stunning safari moments coming soon. Be one of the first to make memories with us.
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {galleryItems.slice(0, 8).map((item, idx) => (
                  <Link
                    key={item.id}
                    to="/safari-library"
                    className="group relative aspect-square rounded-xl overflow-hidden shadow-lg block"
                    data-testid={`home-library-tile-${idx}`}
                  >
                    <img
                      src={resolveImg(item.image_url)}
                      alt={item.caption || 'Safari moment'}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      loading="lazy"
                    />
                    {item.featured && (
                      <div className="absolute top-2 left-2 bg-amber-400 text-emerald-900 text-[10px] font-bold px-2 py-1 rounded-full">
                        ★ FEATURED
                      </div>
                    )}
                    {(item.caption || item.customer_name) && (
                      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent text-white p-3 opacity-0 group-hover:opacity-100 transition">
                        {item.caption && (
                          <p className="font-body text-xs font-medium leading-tight">{item.caption}</p>
                        )}
                        {item.customer_name && (
                          <p className="font-accent text-[10px] text-amber-300 mt-1">— {item.customer_name}</p>
                        )}
                      </div>
                    )}
                  </Link>
                ))}
              </div>

              <div className="text-center mt-12">
                <Link
                  to="/safari-library"
                  data-testid="home-view-library-btn"
                  className="inline-flex items-center space-x-2 border-2 border-navy text-navy hover:bg-navy hover:text-gold rounded-full px-8 py-4 transition-all font-body font-semibold text-lg hover:scale-105"
                >
                  <span>View Full Library</span>
                  <ArrowRight size={20} />
                </Link>
              </div>
            </>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 bg-navy" data-testid="cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-4xl md:text-6xl font-normal leading-tight text-gold mb-6">
            Ready for Your Adventure?
          </h2>
          <p className="font-body text-base md:text-lg text-sand/80 leading-relaxed mb-8">
            Book your Tadoba safari experience today and create memories that last a lifetime
          </p>
          <Link
            to="/packages"
            data-testid="cta-book-btn"
            className="inline-flex items-center space-x-2 bg-emerald-600 hover:bg-emerald-600/90 text-white px-8 py-6 rounded-full font-bold tracking-wide transition-all hover:scale-105"
          >
            <span>Book Your Safari</span>
            <ArrowRight size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;